import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ImageIcon, Plus, Trash2, X } from 'lucide-react'
import { SvgLogo } from '@/features/festivals/components/SvgLogo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminFormPage } from '../components/AdminFormPage'
import { MediaPickerSheet } from '../components/MediaPickerSheet'
import { adminFestivalsService } from '../services/admin.festivals.service'

const festivalSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
  tagline: z.string().min(1, 'Tagline is required'),
  description: z.string().min(1, 'Description is required'),
  isPublished: z.boolean(),
  showTagline: z.boolean(),
  order: z.number().int().min(0),
})

type FestivalFormData = z.infer<typeof festivalSchema>

interface EditionDraft {
  id: string
  year: number
  participants: string[]
  isPublished: boolean
  isNew: boolean
}

function toSlug(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function AdminFestivalEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()

  const [loading, setLoading] = useState(!isNew)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [editions, setEditions] = useState<EditionDraft[]>([])
  const [deletedEditionIds, setDeletedEditionIds] = useState<string[]>([])
  const [newParticipantInputs, setNewParticipantInputs] = useState<Record<string, string>>({})

  // Image state
  const [logoMediaId, setLogoMediaId] = useState<string | undefined>()
  const [logoUrl, setLogoUrl] = useState<string | undefined>()
  const [heroMediaId, setHeroMediaId] = useState<string | undefined>()
  const [heroUrl, setHeroUrl] = useState<string | undefined>()
  const [heroFocusX, setHeroFocusX] = useState(0.5)
  const [heroFocusY, setHeroFocusY] = useState(0.5)

  // Sheet open state
  const [logoSheetOpen, setLogoSheetOpen] = useState(false)
  const [heroSheetOpen, setHeroSheetOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FestivalFormData>({
    resolver: zodResolver(festivalSchema),
    defaultValues: { name: '', slug: '', tagline: '', description: '', isPublished: false, showTagline: true, order: 1 },
  })

  const nameValue = watch('name')

  useEffect(() => {
    if (isNew && nameValue) setValue('slug', toSlug(nameValue), { shouldValidate: false })
  }, [isNew, nameValue, setValue])

  useEffect(() => {
    if (isNew || !id) return
    setLoading(true)
    Promise.all([
      adminFestivalsService.getFestivalById(id),
      adminFestivalsService.getEditions(id),
    ])
      .then(([festival, eds]) => {
        if (festival) {
          reset({
            name: festival.name,
            slug: festival.slug,
            tagline: festival.tagline,
            description: festival.description,
            isPublished: festival.isPublished,
            showTagline: festival.showTagline ?? true,
            order: festival.order,
          })
          setLogoMediaId(festival.logoMediaId)
          setLogoUrl(festival.logoUrl)
          setHeroMediaId(festival.heroMediaId)
          setHeroUrl(festival.heroUrl)
          setHeroFocusX(festival.heroFocusX ?? 0.5)
          setHeroFocusY(festival.heroFocusY ?? 0.5)
        }
        setEditions(
          eds.map((e) => ({
            id: String(e.year),
            year: e.year,
            participants: e.participants,
            isPublished: e.isPublished,
            isNew: false,
          }))
        )
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load festival'))
      )
      .finally(() => setLoading(false))
  }, [id, isNew, reset])

  // ── Edition-level actions ─────────────────────────────────────────────────

  function addEdition() {
    const year = new Date().getFullYear()
    const draft: EditionDraft = { id: String(year), year, participants: [], isPublished: false, isNew: true }
    setEditions((prev) => [...prev, draft])
  }

  function removeEdition(editionId: string, isNewDraft: boolean) {
    setEditions((prev) => prev.filter((e) => e.id !== editionId))
    if (!isNewDraft) setDeletedEditionIds((prev) => [...prev, editionId])
  }

  function updateEditionYear(editionId: string, year: number) {
    setEditions((prev) =>
      prev.map((e) => (e.id === editionId ? { ...e, year, id: String(year) } : e))
    )
  }

  function updateEditionPublished(editionId: string, isPublished: boolean) {
    setEditions((prev) =>
      prev.map((e) => (e.id === editionId ? { ...e, isPublished } : e))
    )
  }

  // ── Participant-level actions ─────────────────────────────────────────────

  function addParticipant(editionId: string) {
    const name = (newParticipantInputs[editionId] ?? '').trim()
    if (!name) return
    setEditions((prev) =>
      prev.map((e) =>
        e.id === editionId ? { ...e, participants: [...e.participants, name] } : e
      )
    )
    setNewParticipantInputs((prev) => ({ ...prev, [editionId]: '' }))
  }

  function updateParticipant(editionId: string, index: number, value: string) {
    setEditions((prev) =>
      prev.map((e) => {
        if (e.id !== editionId) return e
        const updated = [...e.participants]
        updated[index] = value
        return { ...e, participants: updated }
      })
    )
  }

  function removeParticipant(editionId: string, index: number) {
    setEditions((prev) =>
      prev.map((e) =>
        e.id === editionId
          ? { ...e, participants: e.participants.filter((_, i) => i !== index) }
          : e
      )
    )
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async function onSubmit(values: FestivalFormData) {
    setSubmitError(null)
    try {
      let festivalId = id ?? ''
      const imageFields = {
        logoMediaId, logoUrl,
        heroMediaId, heroUrl, heroFocusX, heroFocusY,
      }
      if (isNew) {
        festivalId = await adminFestivalsService.createFestival({ ...values, ...imageFields })
      } else {
        await adminFestivalsService.updateFestival(festivalId, { ...values, ...imageFields })
      }

      await Promise.all(
        deletedEditionIds.map((eid) => adminFestivalsService.deleteEdition(festivalId, eid))
      )
      await Promise.all(
        editions.map((e) =>
          adminFestivalsService.setEdition(festivalId, {
            year: e.year,
            participants: e.participants,
            isPublished: e.isPublished,
          })
        )
      )

      navigate('/admin/config/festivals')
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Failed to save festival'))
    }
  }

  return (
    <AdminFormPage
      title={isNew ? 'New Festival' : 'Edit Festival'}
      cardTitle={isNew ? 'Create a new festival' : 'Update festival details'}
      backTo="/admin/config/festivals"
      loading={loading}
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-6">
        {/* Festival metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="ODDFEST" {...register('name')} />
            {errors.name && <p className="text-sm text-destructive">{withErrorCode(String(errors.name.message), 'app/validation')}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="slug">Slug</Label>
            <Input id="slug" placeholder="oddfest" {...register('slug')} />
            {errors.slug && <p className="text-sm text-destructive">{withErrorCode(String(errors.slug.message), 'app/validation')}</p>}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="tagline">Tagline</Label>
          <Input id="tagline" placeholder="Dance Film Festival" {...register('tagline')} />
          {errors.tagline && <p className="text-sm text-destructive">{withErrorCode(String(errors.tagline.message), 'app/validation')}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" rows={4} placeholder="Describe the festival…" {...register('description')} />
          {errors.description && <p className="text-sm text-destructive">{withErrorCode(String(errors.description.message), 'app/validation')}</p>}
        </div>

        {/* Images */}
        <div className="grid grid-cols-2 gap-6">
          {/* Logo */}
          <div className="space-y-2">
            <Label>Logo</Label>
            {logoUrl ? (
              <div className="relative w-32 h-32 rounded-lg border overflow-hidden bg-muted flex items-center justify-center p-2">
                <SvgLogo url={logoUrl} alt="Festival logo" className="h-full w-auto" />
                <button
                  type="button"
                  onClick={() => { setLogoMediaId(undefined); setLogoUrl(undefined) }}
                  className="absolute top-1 right-1 rounded-full bg-background/80 p-0.5 hover:bg-background"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLogoSheetOpen(true)}
                className="flex h-32 w-32 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
              >
                <ImageIcon className="h-6 w-6" />
                <span className="text-xs">Upload Logo</span>
              </button>
            )}
            {logoUrl && (
              <Button type="button" variant="outline" size="sm" onClick={() => setLogoSheetOpen(true)}>
                Change
              </Button>
            )}
          </div>

          {/* Hero image */}
          <div className="space-y-2">
            <Label>Hero Image</Label>
            {heroUrl ? (
              <div className="relative w-full h-32 rounded-lg border overflow-hidden bg-muted">
                <img
                  src={heroUrl}
                  alt="Festival hero"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `${heroFocusX * 100}% ${heroFocusY * 100}%` }}
                />
                <button
                  type="button"
                  onClick={() => { setHeroMediaId(undefined); setHeroUrl(undefined); setHeroFocusX(0.5); setHeroFocusY(0.5) }}
                  className="absolute top-1 right-1 rounded-full bg-background/80 p-0.5 hover:bg-background"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setHeroSheetOpen(true)}
                className="flex h-32 w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
              >
                <ImageIcon className="h-6 w-6" />
                <span className="text-xs">Upload Hero Image</span>
              </button>
            )}
            {heroUrl && (
              <Button type="button" variant="outline" size="sm" onClick={() => setHeroSheetOpen(true)}>
                Change
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="order">Sort Order</Label>
            <Input id="order" type="number" min={0} {...register('order', { valueAsNumber: true })} />
            {errors.order && <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>}
          </div>
          <div className="flex flex-col gap-2 pb-0.5 justify-end">
            <div className="flex items-center gap-3">
              <input id="isPublished" type="checkbox" className="h-4 w-4 accent-primary" {...register('isPublished')} />
              <Label htmlFor="isPublished">Published</Label>
            </div>
            <div className="flex items-center gap-3">
              <input id="showTagline" type="checkbox" className="h-4 w-4 accent-primary" {...register('showTagline')} />
              <Label htmlFor="showTagline">Show tagline</Label>
            </div>
          </div>
        </div>

        <Separator />

        {/* Editions */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">Editions</h3>
            <Button type="button" variant="outline" size="sm" onClick={addEdition}>
              <Plus className="mr-1 h-4 w-4" /> Add Edition
            </Button>
          </div>

          {editions.length === 0 && (
            <p className="text-sm text-muted-foreground">No editions yet. Add one above.</p>
          )}

          {editions.map((edition) => (
            <div key={edition.id} className="rounded-lg border p-4 space-y-4">
              {/* Edition header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="space-y-1">
                    <Label>Year</Label>
                    <Input
                      type="number"
                      className="w-24"
                      value={edition.year}
                      onChange={(e) => updateEditionYear(edition.id, Number(e.target.value))}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <input
                      type="checkbox"
                      id={`pub-${edition.id}`}
                      className="h-4 w-4 accent-primary"
                      checked={edition.isPublished}
                      onChange={(e) => updateEditionPublished(edition.id, e.target.checked)}
                    />
                    <Label htmlFor={`pub-${edition.id}`}>Published</Label>
                    {edition.isNew && <Badge variant="secondary">New</Badge>}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => removeEdition(edition.id, edition.isNew)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Participants table */}
              <div className="space-y-2">
                <Label>Participants</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {edition.participants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                          No participants yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      edition.participants.map((name, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-muted-foreground w-8">{index + 1}</TableCell>
                          <TableCell>
                            <Input
                              value={name}
                              onChange={(e) => updateParticipant(edition.id, index, e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeParticipant(edition.id, index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {/* Add participant row */}
                <div className="flex gap-2 pt-1">
                  <Input
                    placeholder="Participant name…"
                    value={newParticipantInputs[edition.id] ?? ''}
                    onChange={(e) =>
                      setNewParticipantInputs((prev) => ({ ...prev, [edition.id]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addParticipant(edition.id) }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addParticipant(edition.id)}
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isNew ? 'Create Festival' : 'Save Changes'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/config/festivals')}>
            Cancel
          </Button>
        </div>
      </form>
      <MediaPickerSheet
        open={logoSheetOpen}
        onClose={() => setLogoSheetOpen(false)}
        title="Festival Logo"
        description="Select an SVG logo for this festival. The brand gradient will be applied to the SVG on the public page."
        withFocusPoint={false}
        mimeTypeFilter="image/svg+xml"
        currentMediaId={logoMediaId}
        onAssign={(mediaId, mediaUrl) => { setLogoMediaId(mediaId); setLogoUrl(mediaUrl) }}
      />

      <MediaPickerSheet
        open={heroSheetOpen}
        onClose={() => setHeroSheetOpen(false)}
        title="Hero Image"
        description="Select a hero image. You can set a focus point to control how it crops on different screens."
        withFocusPoint={true}
        currentMediaId={heroMediaId}
        currentFocusX={heroFocusX}
        currentFocusY={heroFocusY}
        onAssign={(mediaId, mediaUrl, focusX, focusY) => {
          setHeroMediaId(mediaId); setHeroUrl(mediaUrl)
          setHeroFocusX(focusX); setHeroFocusY(focusY)
        }}
      />
    </AdminFormPage>
  )
}
